import type { RouteConfig } from "@asteasolutions/zod-to-openapi";
import { defaultErrorResponses, problemResponse, registry } from "../registry.js";
import { z } from "../zod.js";
import {
  ChannelAccount,
  ChannelAccountConnectRequest,
  ChannelAccountDisconnectResponse,
  ChannelAccountListResponse,
  ChannelAccountProbeResponse,
  ChannelIdentity,
  ChannelIdentityListResponse,
  ChannelIdentityStatus,
  ChannelPreference,
  ChannelPreferencePutRequest,
  ChatProvider,
  EmailHeldPartDownloadResponse,
  EmailIntake,
  EmailQuarantineDiscardResponse,
  EmailQuarantineListResponse,
  EmailQuarantineReleaseRequest,
  EmailQuarantineReleaseResponse,
  IdentityRevokeRequest,
  IdentityUpdateRequest,
  LinkCodeIssueRequest,
  LinkCodeIssueResponse,
  Conversation,
  ConversationKind,
  ConversationListResponse,
  ConversationPatchRequest,
  ConversationReadResponse,
  ConversationStatus,
  MediaUploadUrlRequest,
  MediaUploadUrlResponse,
  MessageDetailResponse,
  MessageRetryResponse,
  MessageSendRequest,
  MessageSendResponse,
  MessageTemplate,
  ThreadMessagesResponse,
  ThreadSendRequest,
  WebhookEventLogResponse,
  MessageTemplateListResponse,
  MessageTemplateVariant,
  MessagingHealthResponse,
  MessagingLocale,
  MessagingOwnerKind,
  MessagingProvider,
  TelegramWebhookAck,
  TelegramWebhookUpdate,
  TemplateCategory,
  MessagingTemplateCreateRequest,
  TemplateMetaStatus,
  MessagingTemplateUpdateRequest,
  TemplateVariantPutRequest,
} from "./schemas.js";

// Module 17 — messaging HTTP surface (spec 17 §5), SLICE P3-B: the TELEGRAM
// half live, the WhatsApp machinery dormant behind the adapter seam (D-232).
// Part 1 — provider accounts (connect/list/disconnect/probe + health),
// identity linking (issue/list/revoke/patch), templates (list/create/patch +
// variant upsert), the channel-abstracted send, channel preferences, and the
// PUBLIC Telegram webhook. Part 2 (the office inbox slice) — the conversation
// rows: inbox list, thread head, thread messages, the office reply, PATCH +
// mark-read, media upload-url, message detail + manual retry, and the §4.4
// webhook-event log tail (its §5 row was added WITH this slice —
// spec-is-contract, amended in the same PR). P3-E2 PR E2-2e (D-327) adds
// email intake's seven routes (the intake read and its two switches, the
// Unverified mail list, release and discard, and the held-part download)
// and amends the rows above for an `email` account, thread and message.
// Every request that names a provider takes `ChatProvider`, so none accepts
// more than it did before email existed (D-327 ruling 19).
//
// WHAT IS ABSENT, AND WHY THAT IS THE POINT (D-026/D-033):
//   * variant submit + templates/sync + the WA webhook rows — PARKED with
//     WhatsApp (D-232): they act against a BSP that does not exist, and land
//     at unparking on the owner's own DECISIONS entry;
//   * the platform suspend action — spec 17 §4.6's admin console surface.
// Their paths are absent from openapi/v1.json entirely; a 404 on a route
// that does not exist is honest, a 501 stub is not.
//
// D-222 status parity: creations answer 201 (connect, link-codes, send's
// created-message union), state transitions and reads answer 200. The
// public webhook answers 200 always post-persist (§3.4.2 — providers
// retry on anything else), 401 on a bad secret token, 404 on an unknown
// token (no information leak).

const sessionSecurity = [{ sessionCookie: [] }];
const idempotencyHeader = { $ref: "#/components/parameters/IdempotencyKey" };

function route(config: {
  method: "get" | "post" | "patch" | "put" | "delete";
  path: string;
  operationId: string;
  summary: string;
  permission?: string;
  public?: boolean;
  request?: RouteConfig["request"];
  responses: RouteConfig["responses"];
  description?: string;
}): void {
  const mutation = config.method !== "get";
  registry.registerPath({
    method: config.method,
    path: config.path,
    operationId: config.operationId,
    tags: ["messaging"],
    summary: config.summary,
    description:
      (config.description ?? "") +
      (config.permission !== undefined ? ` Requires \`${config.permission}\`.` : ""),
    ...(config.public === true ? {} : { security: sessionSecurity }),
    ...(config.request !== undefined ? { request: config.request } : {}),
    ...(mutation && config.public !== true ? { parameters: [idempotencyHeader] } : {}),
    responses: { ...defaultErrorResponses(), ...config.responses },
  });
}

const jsonBody = (schema: z.ZodType) => ({
  body: { content: { "application/json": { schema } } },
});
const ok = (description: string, schema: z.ZodType) => ({
  200: { description, content: { "application/json": { schema } } },
});
const created = (description: string, schema: z.ZodType) => ({
  201: { description, content: { "application/json": { schema } } },
});

const IdParam = z.object({ id: z.guid() });

/** Cursor pagination (§5): default 25, max 100 — spec 17's own bounds. */
const cursorQuery = {
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).optional(),
};

// ===========================================================================
// provider accounts + health (spec 17 §2.1/§2.13, §3.1; §5 rows 1–4, health)
// ===========================================================================

route({
  method: "post",
  path: "/v1/messaging/accounts",
  operationId: "connectMessagingAccount",
  summary: "Connect a provider (Telegram live; WhatsApp parked — D-232)",
  permission: "messaging.account.manage",
  description:
    "Spec 17 §3.1 — validates the bot token via getMe, stores it as an SSM SecureString (never in Postgres, §2 preamble), creates the account `connecting`, calls setWebhook with a fresh webhook_token + secret_token, and flips `active` on the first valid webhook. `provider='whatsapp'` answers 422 WA_PARKED until Meta verification clears and the owner's BSP entry lands (D-232); the embedded-signup payload joins the contract at unparking. `provider` names a chat provider only: email intake is turned on through `POST /v1/messaging/email-intake/enable`, and `email` here is refused 400 VALIDATION_FAILED by the schema (D-327 ruling 19).",
  request: jsonBody(ChannelAccountConnectRequest),
  responses: {
    ...created("The connecting account (secrets omitted — §7 contract-level omit)", ChannelAccount),
    409: problemResponse("ACCOUNT_EXISTS — one live account per provider per tenant (§2.1)"),
    422: problemResponse("WA_PARKED / TG_TOKEN_INVALID (provider text in extensions, verbatim)"),
  },
});

route({
  method: "get",
  path: "/v1/messaging/accounts",
  operationId: "listMessagingAccounts",
  summary: "List channel accounts with embedded provider health",
  permission: "messaging.settings.read",
  description:
    "Spec 17 §5. Every account of the tenant with its §2.13 health. Since P3-E2 PR E2-2e the tenant's `email` account is listed too once intake was first turned on (D-327 ruling 19): `display_name` is the intake address, `status` is `active` or `disconnected`, `last_webhook_at` is when its newest mail arrived, and `health` is the receiving rule set's state as this tenant last probed or received it. The §4.4 Telegram and WhatsApp cards skip it, and the Email intake card reads it.",
  responses: {
    ...ok("Accounts + §2.13 health, newest first", ChannelAccountListResponse),
  },
});

route({
  method: "post",
  path: "/v1/messaging/accounts/{id}/disconnect",
  operationId: "disconnectMessagingAccount",
  summary: "Disconnect a channel account",
  permission: "messaging.account.manage",
  description:
    "Spec 17 §3.1 state machine: any → disconnected; calls deleteWebhook through the adapter. The webhook token stops resolving in the same transaction (resolve_webhook_token holds `status <> 'disconnected'` in its WHERE), so a late provider POST 404s. On the tenant's `email` account (D-327 ruling 19) it turns intake off exactly as `POST /v1/messaging/email-intake/disable` does, audited `messaging.email_intake_disabled`, with no adapter call. Mail that arrives afterwards is dropped by the worker, and turning intake on again reactivates the same account and address.",
  request: { params: IdParam },
  responses: {
    ...ok("The account, now disconnected", ChannelAccountDisconnectResponse),
  },
});

route({
  method: "post",
  path: "/v1/messaging/accounts/{id}/probe",
  operationId: "probeMessagingAccount",
  summary: "Manual health probe / webhook re-set",
  permission: "messaging.account.manage",
  description:
    "Re-runs getWebhookInfo and re-issues setWebhook when the registered URL drifted (§4.4's Reconnect); reports the provider's own last-error text verbatim as a typed field. On the tenant's `email` account (D-327 ruling 19) it re-reads the active SES receiving rule set instead and answers in the same shape: `webhook_ok` is true exactly when our rule set is the active one, `provider_detail` is `ruleset_inactive` or `ruleset_check_failed` otherwise and never a rule set's name, and `last_webhook_at` is when the newest mail arrived. It writes the result into this tenant's §2.13 row alone (`healthy`, `down` or `degraded`), emitting `messaging.provider_health_changed` on a change, and leaves every other tenant's row as it was. On a deployment with no inbound mail source bound (no `INBOUND_MAIL_BUCKET`) an email account's probe answers 409 `EMAIL_INTAKE_UNAVAILABLE`, as the enable route does, and writes nothing.",
  request: { params: IdParam },
  responses: {
    ...ok("Probe result", ChannelAccountProbeResponse),
    409: problemResponse(
      "EMAIL_INTAKE_UNAVAILABLE when an email account is probed on a deployment with no inbound mail source",
    ),
  },
});

route({
  method: "get",
  path: "/v1/messaging/health",
  operationId: "getMessagingHealth",
  summary: "Provider health snapshot (state, queued count, oldest queued)",
  permission: "messaging.settings.read",
  description:
    "Spec 17 §5. One item per account. The tenant's `email` account is served in the same item shape (D-327 ruling 19): `account_status` `active` or `disconnected` for intake on or off, `health` its §2.13 row, `queued_count` 0 and `oldest_queued_at` null, because nothing is ever sent through it.",
  responses: {
    ...ok("Per-account §2.13 state + queue posture", MessagingHealthResponse),
  },
});

// ===========================================================================
// identity linking (spec 17 §2.2/§2.3, §3.2; §5 rows 5–8)
// ===========================================================================

route({
  method: "post",
  path: "/v1/messaging/identities/link-codes",
  operationId: "issueMessagingLinkCode",
  summary: "Issue a 6-char identity link code (+ Telegram deep link)",
  permission: "messaging.link.issue",
  description:
    "Spec 17 §3.2.1 — the plaintext code is returned ONCE (SHA-256 stored, §2.3), 15-minute expiry. Every role may issue for their OWN user identity; customer-contact codes are narrowed to sales/facility_admin/group_admin service-side (D-040 two-layer — 403 OWNER_SCOPE_FORBIDDEN). `provider` names a chat provider only. Email never enters the link handshake (D-327 ruling 10), and `email` here is refused 400 VALIDATION_FAILED by the schema (D-327 ruling 19).",
  request: jsonBody(LinkCodeIssueRequest),
  responses: {
    ...created("The one-time plaintext code + deep link", LinkCodeIssueResponse),
    403: problemResponse("OWNER_SCOPE_FORBIDDEN — customer-contact issue outside sales/admins"),
    422: problemResponse("OWNER_NOT_FOUND"),
  },
});

route({
  method: "get",
  path: "/v1/messaging/identities",
  operationId: "listMessagingIdentities",
  summary: "List channel identities (addresses masked per §7 field grant)",
  permission: "messaging.identity.manage",
  description:
    "Spec 17 §5. `provider` filters by any provider, `email` included since P3-E2 PR E2-2e (D-327 ruling 19). An email identity's `address` is masked as `•••@domain`, and its `provider_profile_name`, the From display name, is null for a caller without messaging.address.read (D-327 rulings 13 and 20).",
  request: {
    query: z.object({
      owner_kind: MessagingOwnerKind.optional(),
      owner_id: z.guid().optional(),
      provider: MessagingProvider.optional(),
      status: ChannelIdentityStatus.optional(),
      ...cursorQuery,
    }),
  },
  responses: {
    ...ok(
      "Identities; `address` masked unless the caller holds messaging.address.read",
      ChannelIdentityListResponse,
    ),
  },
});

route({
  method: "post",
  path: "/v1/messaging/identities/{id}/revoke",
  operationId: "revokeMessagingIdentity",
  summary: "Revoke an identity binding",
  permission: "messaging.identity.revoke",
  description:
    "Spec 17 §3.2 state machine — no transition out of `revoked`; re-linking creates a new row. Emits `messaging.identity_revoked` (module 09 clears `contact_channels.is_verified` off it — the spec 09 §6 mirror). Every role may revoke their OWN identity; customer identities need sales/admins and other users' identities need admins, service-side (D-040).",
  request: { params: IdParam, ...jsonBody(IdentityRevokeRequest) },
  responses: {
    ...ok("The identity, now revoked", ChannelIdentity),
    403: problemResponse("OWNER_SCOPE_FORBIDDEN"),
    409: problemResponse("IDENTITY_ALREADY_REVOKED"),
  },
});

route({
  method: "patch",
  path: "/v1/messaging/identities/{id}",
  operationId: "updateMessagingIdentity",
  summary: "Update identity language / soft opt-out",
  permission: "messaging.identity.manage",
  description:
    "Spec 17 §5 — clearing `opted_out_hard` is impossible through the API by construction (the request schema has no such key, §9.15): only the person re-opts-in by messaging START.",
  request: { params: IdParam, ...jsonBody(IdentityUpdateRequest) },
  responses: {
    ...ok("The updated identity", ChannelIdentity),
  },
});

// ===========================================================================
// templates (spec 17 §2.4/§2.5; §5 rows 9–12 — the P3 authoring surface;
// Meta submit/sync are parked with WhatsApp, D-232)
// ===========================================================================

route({
  method: "get",
  path: "/v1/messaging/templates",
  operationId: "listMessagingTemplates",
  summary: "List templates with their provider×locale variant matrix",
  permission: "messaging.template.read",
  description:
    "The system templates (the four order-status templates — confirmed, in production, ready, shipped — quote_offer, approval_request, link_welcome, re_engage, payment_reminder) are ensured present for the tenant on first read/connect — §4.3's 'empty state: impossible', done as data rather than screen prose. Ordered by `code` then `id` and cursor-paged in the house shape: `limit` (default 25, at most 100) rows a page, and `next_cursor` is the opaque cursor of the next page or null on the last one (task #278). `is_active=true` answers only active templates and `is_active=false` only deactivated ones; §4.3's catalog reads the active ones by default and the deactivated ones only when the reader asks for them.",
  request: {
    query: z.object({
      category: TemplateCategory.optional(),
      is_active: z.enum(["true", "false"]).optional(),
      meta_status: TemplateMetaStatus.optional(),
      ...cursorQuery,
    }),
  },
  responses: {
    ...ok("Templates + variants", MessageTemplateListResponse),
  },
});

route({
  method: "post",
  path: "/v1/messaging/templates",
  operationId: "createMessagingTemplate",
  summary: "Create a template (logical intent; variants via PUT)",
  permission: "messaging.template.manage",
  request: jsonBody(MessagingTemplateCreateRequest),
  responses: {
    ...created("The created template (no variants yet)", MessageTemplate),
    409: problemResponse("TEMPLATE_EXISTS"),
  },
});

route({
  method: "patch",
  path: "/v1/messaging/templates/{id}",
  operationId: "updateMessagingTemplate",
  summary: "Edit template meta (name, variables, active flag)",
  permission: "messaging.template.manage",
  request: { params: IdParam, ...jsonBody(MessagingTemplateUpdateRequest) },
  responses: {
    ...ok("The updated template", MessageTemplate),
    422: problemResponse(
      "SYSTEM_TEMPLATE_PROTECTED — system templates cannot be deactivated; VALIDATION_FAILED — the new `variables` would leave a variant's url button that rendered before rendering no longer, by taking away a variable its link uses, giving it a sample that does not render, or making a variable the link needs optional (task #278); each errors[] entry has path `variables`, message_key `messaging.validation.button_url.{code}` and params `{index, label, token, provider, locale}`",
    ),
  },
});

route({
  method: "put",
  path: "/v1/messaging/templates/{id}/variants/{provider}/{locale}",
  operationId: "putMessagingTemplateVariant",
  summary: "Upsert a variant body for (provider, locale)",
  permission: "messaging.template.manage",
  description:
    "Spec 17 §5 — Telegram variants stay `not_required` and are sendable immediately; upserting a WhatsApp variant resets it to `draft` (dormant Meta bookkeeping while D-232 stands, exercised through the stub adapter). A Telegram body is checked against §2.5's HTML subset before anything is written: `<b>`, `<i>`, `<code>` and `<a href>` (an http(s):// or tg:// URL, or one that starts with a `{{variable}}`), balanced and properly nested, with only ASCII spaces, tabs and line breaks inside a tag, every other `<`, `>` and `&` written as `&lt;`, `&gt;`, `&amp;`, `&quot;` or a numeric entity, and some visible text. A body outside it answers 422 VALIDATION_FAILED, because Telegram would refuse every send of it, or, for a bare `&` or `>` (which the Bot API requires escaped, and where Telegram decodes `&lt`, `&gt`, `&amp` and `&quot` even without the `;`), could show the customer something other than what was typed; each `errors[]` entry has `path` `body`, a `code` naming the rule, `message_key` `messaging.validation.telegram_html.{code}` and `params` `{offset, token}`, the offending tag or character and its UTF-16 offset in the body, at most 20 of them, and `detail` names the first. A WhatsApp body is plain text and is not judged by these rules. A `url` button's `url` is a template too (task #278): rendered with the template's sample values, a placeholder that opens the URL supplying it whole and every other value percent-encoded, it must come out as an absolute `https://` URL with a real host name, or a `tg://` URL on a Telegram variant, with no user name or password. A url that does not answers 422 VALIDATION_FAILED with one `errors[]` entry per button, `path` `buttons.{i}.url`, a `code` (`url_variable_unknown` for a placeholder the template does not declare, `url_variable_optional` for a variable the link needs that is not required, since a message sent with it blank would have no link, `url_invalid`, `url_scheme_not_allowed`), `message_key` `messaging.validation.button_url.{code}` and `params` `{index, label, token}`, and `detail` names the first button by its label. A value that is only `.` or `..` cannot be placed in a link except where it opens it, because the URL parser would move the link, so the samples must not be. The `{provider}` segment names a chat provider only, because email carries no template (D-327 ruling 19), and `email` there is refused 400 VALIDATION_FAILED.",
  request: {
    params: z.object({
      id: z.guid(),
      provider: ChatProvider,
      locale: MessagingLocale,
    }),
    ...jsonBody(TemplateVariantPutRequest),
  },
  responses: {
    ...ok("The upserted variant", MessageTemplateVariant),
    422: problemResponse(
      "VALIDATION_FAILED — a Telegram body outside spec 17 §2.5's HTML subset (errors[] names each offending tag or character with its offset, params.offset and params.token), or a url button whose URL does not render with the template's samples (errors[] path buttons.{i}.url, params.index, params.label and params.token)",
    ),
  },
});

// ===========================================================================
// channel-abstracted send (spec 17 §3.3; §5 /messages/send)
// ===========================================================================

route({
  method: "post",
  path: "/v1/messaging/messages/send",
  operationId: "sendMessagingMessage",
  summary: "Channel-abstracted template send (§3.3 entry)",
  permission: "messaging.send",
  description:
    "Spec 17 §3.3 — resolves the recipient via channel_preferences (primary → fallback → ANY verified identity; §3.3.1), runs the policy gates in order (revoked / opted_out_hard / marketing-vs-opt-out / WA session window with template auto-upgrade / variant locale chain — all §3.3.2), renders with required-variable validation, persists `queued` (or `suppressed` with the gate's typed detail) and dispatches via outbox → SQS → the per-account token-bucket worker. Message id doubles as the Idempotency-Key echo (§2.7). In P3 the callers are module 09's status notifications and this module's own machinery; module 12 arrives P4 (charter stated absences).",
  request: jsonBody(MessageSendRequest),
  responses: {
    ...created(
      "The persisted message — `queued`, or `suppressed` by a policy gate",
      MessageSendResponse,
    ),
    409: problemResponse(
      "NO_CHANNEL — no verified chat identity on any provider; nothing persisted",
    ),
    422: problemResponse(
      "TEMPLATE_NOT_FOUND / TEMPLATE_VARS_INVALID (a required variable with no value, errors[] code `required`, or a value only of dots in the middle of a body link, which would move it, code `link_value_invalid`) / OWNER_NOT_FOUND",
    ),
  },
});

// ===========================================================================
// channel preferences (spec 17 §2.12; §5 rows)
// ===========================================================================

const preferenceParams = z.object({
  owner_kind: MessagingOwnerKind,
  owner_id: z.guid(),
});

route({
  method: "get",
  path: "/v1/messaging/preferences/{owner_kind}/{owner_id}",
  operationId: "getMessagingPreference",
  summary: "Get a principal's channel preference (defaults when no row)",
  permission: "messaging.preferences.manage",
  description:
    "Every role reads their OWN preference; customer-contact and other-user reads are narrowed to sales/admins service-side (D-040). `explicit: false` carries the no-row case as a typed fact — the §2.12 column defaults are what resolution uses.",
  request: { params: preferenceParams },
  responses: {
    ...ok("The preference (or the stated defaults)", ChannelPreference),
    403: problemResponse("OWNER_SCOPE_FORBIDDEN"),
  },
});

route({
  method: "put",
  path: "/v1/messaging/preferences/{owner_kind}/{owner_id}",
  operationId: "putMessagingPreference",
  summary: "Set a principal's channel preference",
  permission: "messaging.preferences.manage",
  description:
    "Spec 17 §2.12. `primary_provider` and `fallback_provider` name chat providers only, because email is never a send preference (D-327 ruling 19), and `email` in either is refused 400 VALIDATION_FAILED by the schema.",
  request: { params: preferenceParams, ...jsonBody(ChannelPreferencePutRequest) },
  responses: {
    ...ok("The stored preference", ChannelPreference),
    403: problemResponse("OWNER_SCOPE_FORBIDDEN"),
    422: problemResponse("OWNER_NOT_FOUND"),
  },
});

// ===========================================================================
// conversations — the office inbox (spec 17 §4.1/§4.2; §5 — PART 2)
// ===========================================================================

route({
  method: "get",
  path: "/v1/messaging/conversations",
  operationId: "listMessagingConversations",
  summary: "Conversation inbox (filters per §5; sorted last_inbound_at DESC)",
  permission: "messaging.conversation.read",
  description:
    "Spec 17 §5 — the §4.1 thread list. `q` matches the contact's profile name and the customer's display name for every reader. It matches the customer's legal name only for a caller the customer book serves it to (`sales.read` with a customer-read role; a `sales`-only principal on its assigned customers), so qc, which module 09 refuses the book, is matched on the display name alone (the customer leg runs through the module-09 service seam, #272). It matches the phone number or chat id by its tail only for a caller holding `messaging.address.read`: a reader served the address masked is matched on profile name and customer name only, because the address leg is left out of their query, so no digit of a masked address decides which threads come back, the digits the mask shows included (§7, #272). `q` matches no order number; §5's order-no leg is not built. `unclaimed=true` is §4.2's tab: customer threads with no attached customer. `assigned=me|none` is the P3-0-adjudicated pair behind the Mine/Unassigned tabs — `me` resolves to the caller server-side (the client has no self-id feed), `none` is the unassigned set; `assigned_to` stays the uuid form for callers that hold one. D-142: exactly these filters, nothing undeclared. Threads with no inbound yet order by creation time in the same descending key. D-301: `last_message_preview` holds the customer's last inbound words and never an outbound body, so no reader is shown a total or a tracking link in the list, and `q` never searches message bodies (a body search would let a reader denied a total probe for its digits). A message the customer forwarded is not the customer's words and never becomes the preview, which keeps the words before it (D-337 ruling 5). Email threads (P3-E2 PR E2-2e, D-327) are listed with the chat threads, and `provider=email` selects them. Their preview is the subject and then the body's first words, never the quoted part, and a held mail never moves it. For a caller without `messaging.address.read`, `q` matches neither an email address nor an email display name, which that caller is served masked and null (D-327 ruling 20, extending D-306).",
  request: {
    query: z.object({
      status: ConversationStatus.optional(),
      assigned: z.enum(["me", "none"]).optional(),
      assigned_to: z.guid().optional(),
      kind: ConversationKind.optional(),
      customer_id: z.guid().optional(),
      provider: MessagingProvider.optional(),
      q: z.string().min(1).max(120).optional(),
      unclaimed: z.enum(["true"]).optional(),
      ...cursorQuery,
    }),
  },
  responses: {
    ...ok("Conversations, addresses masked per §7 field grant", ConversationListResponse),
  },
});

route({
  method: "get",
  path: "/v1/messaging/conversations/{id}",
  operationId: "getMessagingConversation",
  summary: "Thread head: conversation + identity (masked) + customer + wa_window",
  permission: "messaging.conversation.read",
  request: { params: IdParam },
  responses: {
    ...ok("The §4.1 thread head", Conversation),
  },
});

route({
  method: "get",
  path: "/v1/messaging/conversations/{id}/messages",
  operationId: "listMessagingThreadMessages",
  summary: "Thread messages, cursor-paginated descending",
  permission: "messaging.conversation.read",
  description:
    "Spec 17 §5 — newest first for §4.1's infinite upward scroll; media items carry signed GET URLs minted per-request post-RLS (§9.12): the file registry's 5-minute URL, served as an attachment, for a registry file (D-322), and a 15-minute URL for a row that still holds its pre-0076 key until the re-home moves it. A client that holds a thread open longer than that re-reads the page for fresh links. D-301: `body` is fenced per reader. A reader without `sales.read_value` receives a template message's marked values (a quote's total, an order's tracking link) masked as `•••`, or the body withheld whole (null) where it cannot be re-rendered faithfully, with `value_withheld` true and `withheld_reason` `value`. An inbound message the customer forwarded is withheld whole from the same reader, `body` null and `media` empty with no URL minted, with `value_withheld` true and `withheld_reason` `forwarded` (D-337 ruling 5), because a forward is often our own quote or invoice sent back. A quote's accept link is served to no reader (D-585): it reads `•••` for a holder of `sales.read_value` too, with `withheld_reason` `credential`, because the link accepts the offer as the customer. A priced document the platform sent with a message, the quote PDF, is left out of `media` for a reader without `sales.read_value`, with `media_withheld` true and no URL minted. The customer's own words and text a person typed are served as they are. An email message (P3-E2 PR E2-2e, D-327) carries its `subject`, `body_truncated`, `sender_verified`, `email_receipt_id`, `released_by_name`, `held_parts` and `omitted_parts`, and its stored parts are media items with their `file_name`. Its `body` is the sender's new text, served as written. Its quoted or forwarded part, `quoted_body`, is served only to a holder of `sales.read_value`, with every address in it masked for a holder without `messaging.address.read`, and every other reader reads `has_quoted_text` alone (D-327 ruling 12).",
  request: { params: IdParam, query: z.object({ ...cursorQuery }) },
  responses: {
    ...ok("Messages with media + delivery ticks", ThreadMessagesResponse),
  },
});

route({
  method: "post",
  path: "/v1/messaging/conversations/{id}/messages",
  operationId: "sendMessagingThreadReply",
  summary: "Office reply into a thread: free-text, media, or template",
  permission: "messaging.conversation.manage",
  description:
    "Spec 17 §5/§3.3.3 — the §4.1 composer. `template_code` is mutually exclusive with `body`/`media_s3_keys`, and at least one of the three is required — refused at the contract layer (400 VALIDATION_FAILED, the platform-wide malformed-request posture; §5 amended). The template path resolves a sendable variant down the §3.3.2 locale chain and runs the same policy gates as §3.3; a WA free-form send outside the 24h session window answers 409 WA_WINDOW_CLOSED and the client retries with `template_code`. Media keys must be this tenant's messaging-uploads prefix and pass the 25 MB / mime-allowlist checks against the STORED object (§3.3.3) — message_media rows land `fetched`. Message id doubles as the Idempotency-Key echo (§2.7). `body` is plain text (task #278): it is stored and served as typed, and the send worker writes its `&`, `<` and `>` as entities for Telegram at dispatch, so the customer reads exactly what was typed. A queued message the provider would refuse, because its text is empty once the characters Telegram ignores are trimmed or longer than 4096 characters as Telegram counts them, is failed before the provider call with `status_detail` `body_empty` or `body_too_long:{length}`. The length is counted with only the spaces and line breaks at its ends trimmed, so a doubt refuses before the call.",
  request: { params: IdParam, ...jsonBody(ThreadSendRequest) },
  responses: {
    ...created(
      "The persisted reply — `queued`, or `suppressed` by a policy gate",
      MessageSendResponse,
    ),
    409: problemResponse(
      "CHANNEL_INBOUND_ONLY when the thread is an email conversation, which is inbound only (D-327 ruling 9). It is the second of the thread refusals, after the 404 and before the reply-target 422, so nothing is checked, copied or persisted first / WA_WINDOW_CLOSED / NO_CHANNEL (revoked thread identity)",
    ),
    422: problemResponse(
      "TEMPLATE_NOT_FOUND / TEMPLATE_VARS_INVALID (a required variable with no value, errors[] code `required`, or a value only of dots in the middle of a body link, which would move it, code `link_value_invalid`) / MEDIA_TOO_LARGE / MEDIA_TYPE_UNSUPPORTED / MEDIA_KEY_INVALID",
    ),
  },
});

route({
  method: "patch",
  path: "/v1/messaging/conversations/{id}",
  operationId: "updateMessagingConversation",
  summary: "Assign / snooze / close / claim / set primary order",
  permission: "messaging.conversation.manage",
  description:
    "Spec 17 §5 — §2.6's CHECK pairs `status='snoozed'` with `snoozed_until` (transcribed into the request schema). `assigned_to` accepts a member uuid, `null`, or the literal `\"me\"` (resolved to the caller server-side — the P3-0 self-assign arm; the client has no self-id feed). The claim arm (`customer_contact_id`) is §3.4.3's manual path: promotes the thread's PENDING identity to verified for that contact, derives the thread's customer, emits `messaging.identity_verified` + `messaging.conversation_opened` (source `manual`) and audits both — a thread whose identity is already verified answers 409. On an email thread (D-327 rulings 7 and 17) the claim also writes module 09's `email` channel through the handshake writer and, in the same transaction, copies each held part of the sender's receipts into the registry, moving a part whose held object has expired into `omitted_parts` as `expired_before_linking`. `primary_order_id` stores the reference; module 09's order read seam joins with P3-C.",
  request: { params: IdParam, ...jsonBody(ConversationPatchRequest) },
  responses: {
    ...ok("The updated thread head", Conversation),
    409: problemResponse("CONVERSATION_ALREADY_CLAIMED"),
    422: problemResponse("OWNER_NOT_FOUND — assignee not a member / contact missing or archived"),
  },
});

route({
  method: "post",
  path: "/v1/messaging/conversations/{id}/read",
  operationId: "markMessagingConversationRead",
  summary: "Zero the unread counter",
  permission: "messaging.conversation.manage",
  request: { params: IdParam },
  responses: {
    ...ok("Counter zeroed", ConversationReadResponse),
  },
});

// ===========================================================================
// media upload + message detail/retry (spec 17 §5 — PART 2)
// ===========================================================================

route({
  method: "post",
  path: "/v1/messaging/media/upload-url",
  operationId: "createMessagingMediaUploadUrl",
  summary: "Presigned 15-min S3 PUT for an outbound attachment",
  permission: "messaging.conversation.manage",
  description:
    "Spec 17 §5 — key shape `tenants/{tenant_id}/messaging/uploads/{uuid}.{ext}`. The key is a staging key (D-322 ruling 1). The declared mime type is SIGNED into the PUT. At send persist the stored object's size and type are re-checked, the bytes are sniffed against the declared type, and they are copied into the D-064 file registry under the purpose `messaging_media`. The persisted message media row names that registry file, so nothing reads the staging key afterwards.",
  request: jsonBody(MediaUploadUrlRequest),
  responses: {
    ...created("The presigned PUT + tenant-prefixed key", MediaUploadUrlResponse),
    422: problemResponse("MEDIA_TOO_LARGE / MEDIA_TYPE_UNSUPPORTED"),
  },
});

route({
  method: "get",
  path: "/v1/messaging/messages/{id}",
  operationId: "getMessagingMessage",
  summary: "Message detail + §2.9 delivery events",
  permission: "messaging.conversation.manage",
  description:
    "Spec 17 §5 — roles sales/fa/ga: exactly messaging.conversation.manage's set, so the read mounts under it rather than minting a second code for the same distinct set (D-074; recorded in the permissions catalog comment). D-301: the message's `body` is fenced exactly as on the thread read, so a custom role holding the manage code without `sales.read_value` is served the masked body.",
  request: { params: IdParam },
  responses: {
    ...ok("The message + its delivery evidence trail", MessageDetailResponse),
  },
});

route({
  method: "post",
  path: "/v1/messaging/messages/{id}/retry",
  operationId: "retryMessagingMessage",
  summary: "Manual retry of a failed message",
  permission: "messaging.conversation.manage",
  description:
    "Spec 17 §5 — re-queues a `failed_retryable` message immediately, or a `failed_permanent` one whose failure class was retryable exhaustion (the §3.3.7 backoff ran out); the attempt budget resets so the manual act buys a full fresh cycle. Anything else answers 409.",
  request: { params: IdParam },
  responses: {
    ...ok("Back in the queue", MessageRetryResponse),
    409: problemResponse("MESSAGE_NOT_RETRYABLE"),
  },
});

// ===========================================================================
// webhook event log tail (spec 17 §4.4; §5 row added WITH this slice)
// ===========================================================================

route({
  method: "get",
  path: "/v1/messaging/webhook-events",
  operationId: "listMessagingWebhookEvents",
  summary: "Webhook event log tail (last ≤50) for the settings screen",
  permission: "messaging.settings.read",
  description:
    "Spec 17 §4.4 — time, kind (derived from the payload's top-level keys, a typed classification), processing_status; the raw payload rides ONLY on `error` rows (the §4.4 payload viewer), and only to a reader holding both `sales.read_value` (D-301: a provider update can quote our own outbound words, a total or a tracking link, back inside it) and `messaging.address.read` (#272: every update carries the sender's chat id or phone number in clear, and §7 masks the address for a caller without that grant). A reader missing either gets the row and its `error` without the payload. The §5 row for this route was added with this slice (spec-is-contract) — 0052's messaging.settings.read description promised the webhook log from day one.",
  request: {
    query: z.object({ limit: z.coerce.number().int().min(1).max(50).optional() }),
  },
  responses: {
    ...ok("Newest-first tail", WebhookEventLogResponse),
  },
});

// ===========================================================================
// email intake (spec 17 §2.1, §2.14, §3.4's email arm, §4.2, §4.4; §5, P3-E2
// PR E2-2e, D-327). Inbound only, with one monitored address per tenant, the
// Unverified mail list and its two acts, and the held-part download. Mail
// itself arrives through SES receiving and the in-process worker, never
// through a route.
// ===========================================================================

route({
  method: "get",
  path: "/v1/messaging/email-intake",
  operationId: "getMessagingEmailIntake",
  summary: "The tenant's email intake: on or off, and its address",
  permission: "messaging.settings.read",
  description:
    "Spec 17 §5 (D-327 ruling 5). `enabled` is true while the tenant's `email` account is `active`. `address` is `sales@{sub}.assessly.ai` (`sales@{sub}.staging.assessly.ai` on staging), minted from the tenant's subdomain the first time intake is turned on and fixed after it, and `address` and `account_id` are null until then. The §4.4 Email intake card reads it.",
  responses: {
    ...ok("The intake state", EmailIntake),
  },
});

route({
  method: "post",
  path: "/v1/messaging/email-intake/enable",
  operationId: "enableMessagingEmailIntake",
  summary: "Turn email intake on",
  permission: "messaging.account.manage",
  description:
    "Spec 17 §5 and §2.1 (D-327 rulings 5 and 19). The first enable mints the address from the tenant's subdomain and writes the tenant's `email` account `active`, with its §2.13 health row. A later enable sets the same account `active` again, so the address, the identities, the threads and the receipts stay on one account. An enabled tenant answers 200 with no write. Audited `messaging.email_intake_enabled` with `account_id`.",
  responses: {
    ...ok("The intake state, on", EmailIntake),
    409: problemResponse(
      "EMAIL_INTAKE_UNAVAILABLE when this deployment configures no inbound mail source",
    ),
  },
});

route({
  method: "post",
  path: "/v1/messaging/email-intake/disable",
  operationId: "disableMessagingEmailIntake",
  summary: "Turn email intake off",
  permission: "messaging.account.manage",
  description:
    "Spec 17 §5 (D-327 ruling 5). Sets the tenant's `email` account `disconnected`, as `POST /v1/messaging/accounts/{id}/disconnect` on that account does. Mail that arrives while intake is off is dropped by the worker before any tenant row is written. A tenant whose intake is off, or was never on, answers 200 with no write. Audited `messaging.email_intake_disabled` with `account_id`.",
  responses: {
    ...ok("The intake state, off", EmailIntake),
  },
});

route({
  method: "get",
  path: "/v1/messaging/email-quarantine",
  operationId: "listMessagingEmailQuarantine",
  summary: "Unverified mail: the held mail waiting for a person",
  permission: "messaging.conversation.manage",
  description:
    "Spec 17 §5 and §4.2 (D-327 rulings 7 and 18). Lists the tenant's `quarantined` receipts, newest first, keyset-paged on `received_at` then `id` in the module's paging (`limit` 25 by default, at most 100). A mail is held when a known sender's DMARC verdict was not PASS or its From did not hold exactly one mailbox, and a held mail never reaches a thread, the inbox preview, an engine trigger or an agent until a person releases it. `senders` and every address are served in clear to a holder of `messaging.address.read` and masked for every other caller (D-327 ruling 13). No item serves a held object's key or the receipt's raw object key.",
  request: { query: z.object({ ...cursorQuery }) },
  responses: {
    ...ok("Held mail, newest first", EmailQuarantineListResponse),
  },
});

route({
  method: "post",
  path: "/v1/messaging/email-quarantine/{id}/release",
  operationId: "releaseMessagingEmailQuarantine",
  summary: "Release a held mail onto its sender's thread or into triage",
  permission: "messaging.conversation.manage",
  description:
    "Spec 17 §5 and §3.4's email arm, step 8 (D-327 ruling 18). The person's trust stands where DMARC's verdict would have, and the sender is resolved again at release. A verified identity at the chosen mailbox takes the mail on its thread. An address module 09 holds on a contact with no verified email identity on the account gets its identity and lands on that contact's thread. Any other address lands in triage, as an unknown sender's mail does, with its parts held until a person links the sender. Only a release onto a known thread copies the held parts into the registry. The released message carries `sender_verified` false and names its releaser, and the release emits what a landing emits, all in one transaction. `mailbox_index` names the mailbox by its index into the receipt's mailboxes and may be left out when there is exactly one. A receipt already released answers 200 with its message and conversation and writes nothing. The refusals run in the order 403, 404 EMAIL_RECEIPT_NOT_FOUND, 409 EMAIL_RECEIPT_NOT_HELD and 422 EMAIL_RELEASE_MAILBOX_INVALID. Audited `messaging.email_released` with `receipt_id`, `conversation_id` and `message_id`, and never the mailbox chosen.",
  request: { params: IdParam, ...jsonBody(EmailQuarantineReleaseRequest) },
  responses: {
    ...ok("The released message and its thread", EmailQuarantineReleaseResponse),
    404: problemResponse("EMAIL_RECEIPT_NOT_FOUND"),
    409: problemResponse(
      "EMAIL_RECEIPT_NOT_HELD when the receipt was discarded, which another person has decided, or was never held",
    ),
    422: problemResponse(
      "EMAIL_RELEASE_MAILBOX_INVALID when `mailbox_index` is missing for a From with several mailboxes or names none of them, and on every release of a mail whose From parsed to no mailbox, which can only be discarded",
    ),
  },
});

route({
  method: "post",
  path: "/v1/messaging/email-quarantine/{id}/discard",
  operationId: "discardMessagingEmailQuarantine",
  summary: "Discard a held mail",
  permission: "messaging.conversation.manage",
  description:
    "Spec 17 §5 (D-327 ruling 7). Clears the held subject and text and sets the receipt `discarded`. The held parts' objects expire with the `held/` prefix. A receipt already discarded answers 200 and writes nothing. The refusals run in the order 403, 404 EMAIL_RECEIPT_NOT_FOUND and 409 EMAIL_RECEIPT_NOT_HELD. Audited `messaging.email_discarded` with `receipt_id`, and `conversation_id` when the sender has a thread.",
  request: { params: IdParam },
  responses: {
    ...ok("Discarded", EmailQuarantineDiscardResponse),
    404: problemResponse("EMAIL_RECEIPT_NOT_FOUND"),
    409: problemResponse(
      "EMAIL_RECEIPT_NOT_HELD when the receipt was released, which another person has decided, or was never held",
    ),
  },
});

route({
  method: "get",
  path: "/v1/messaging/email-receipts/{id}/parts/{index}/download",
  operationId: "downloadMessagingEmailHeldPart",
  summary: "A 5-minute download link for a held email part",
  permission: "messaging.conversation.manage",
  description:
    "Spec 17 §5 (D-327 ruling 7). `{id}` is the `receipt_id` a held part carries, on a thread message's `held_parts` or an Unverified mail row, and `{index}` is the part's index. Answers a 5-minute GET on the part's `held/` object, served as an attachment under a name derived from its declared type. A part that has moved into the registry is downloaded through the thread's media link instead. 404 EMAIL_RECEIPT_NOT_FOUND for a receipt this tenant does not have, an index it does not hold, a part already moved into the registry, and a part whose held object has expired.",
  request: {
    params: z.object({
      id: z.guid(),
      // A path segment is never null, so the published type is the integer
      // alone (a bare z.coerce.number() publishes `["integer", "null"]`).
      index: z.coerce.number().int().min(0).openapi({ type: "integer", minimum: 0 }),
    }),
  },
  responses: {
    ...ok("The link and when it expires", EmailHeldPartDownloadResponse),
    404: problemResponse("EMAIL_RECEIPT_NOT_FOUND"),
  },
});

// ===========================================================================
// the public Telegram webhook (spec 17 §3.4; §5) — the D-217-shape door
// ===========================================================================

registry.registerPath({
  method: "post",
  path: "/v1/webhooks/messaging/telegram/{webhook_token}",
  operationId: "telegramWebhook",
  tags: ["messaging"],
  summary: "Telegram Bot API webhook (public; secret-token verified)",
  description:
    "Spec 17 §3.4 — the platform's second public door (the CoA link is the first): the token resolves (tenant, account, provider) through the SECURITY DEFINER `resolve_webhook_token` BEFORE any tenant transaction opens (§9.12 — the one sanctioned untenanted read); X-Telegram-Bot-Api-Secret-Token is compared inside the tenant transaction; the raw update is persisted to `webhook_events` and parsing is async (SQS). Always 200 within 2s post-persist — Telegram retries aggressively on anything else; a duplicate update_id answers 200 off the §2.10 unique. 401 counts toward `webhook_failing` (§3.1). The WhatsApp webhook row lands at unparking (D-232). No session, no Idempotency-Key — the provider is the caller.",
  request: {
    params: z.object({ webhook_token: z.string().min(16).max(128) }),
    body: { content: { "application/json": { schema: TelegramWebhookUpdate } } },
  },
  responses: {
    200: {
      description: "Persisted (or deduplicated) — always, so the provider stops retrying",
      content: { "application/json": { schema: TelegramWebhookAck } },
    },
    400: problemResponse(
      "VALIDATION_FAILED: the path token is not 16 to 128 characters, or the body is not a Telegram update (`update_id` an integer). Nothing is persisted, and `instance` carries the route pattern, never the token (D-339)",
    ),
    401: problemResponse("Secret token mismatch (counts toward webhook_failing)"),
    403: problemResponse(
      "CSRF_ORIGIN_INVALID: the POST carries an `Origin` or `Referer` whose host is not the request's host. The platform's origin check runs on every mutation before routing (spec 16 §3.1.3); Telegram sends neither header and never meets it, while a browser posting from another site does. Nothing is persisted (D-339)",
    ),
    404: problemResponse("Unknown or disconnected webhook token (no information leak)"),
  },
});
